import React, { useMemo, useState } from 'react';
import { getBoardSections, Locale } from '../studioLocale';
import { StudioIcon } from './StudioIcons';
import './StoryMap.css';

interface Props {
  elements: any[];
  connections: any[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEdit: (element: any) => void;
  locale: Locale;
}

type View = 'story' | 'people' | 'world' | 'relations';

const normalizeType = (type: string) =>
  type === 'world' ? 'world_state' : type === 'device' ? 'plot_device' : type;

const titleOf = (item: any): string =>
  String(item?.name || item?.data?.name || item?.data?.title || item?.id || '').trim();

const summaryOf = (item: any): string =>
  String(
    item?.data?.description ||
      item?.data?.purpose ||
      item?.data?.scene_goal ||
      item?.data?.motivation ||
      item?.data?.content ||
      item?.data?.premise ||
      item?.data?.note ||
      ''
  ).trim();

const numberIn = (value: unknown): number => {
  const match = String(value || '').match(/\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

const actOrder = (value: unknown): number => {
  const known: Record<string, number> = {
    setup: 1, act_one: 1, act_two: 2, act_three: 3,
    act_four: 4, act_five: 5, resolution: 6,
  };
  return known[String(value || '').toLowerCase()] || numberIn(value);
};

const orderOf = (item: any): number[] => [
  numberIn(item?.data?.episode || item?.data?.episode_number),
  actOrder(item?.data?.act || item?.data?.act_type),
  numberIn(item?.data?.scene_number || item?.data?.sequence || item?.data?.order),
];

const byNarrativeOrder = (a: any, b: any) => {
  const left = orderOf(a);
  const right = orderOf(b);
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return left[index] - right[index];
  }
  return 0;
};

const groupFor = (type: string): View => {
  const normalized = normalizeType(type);
  if (normalized === 'scene' || normalized === 'event' || normalized === 'dialogue') return 'story';
  if (normalized === 'character' || normalized === 'arc_milestone') return 'people';
  return 'world';
};

const StoryMap: React.FC<Props> = ({ elements, connections, selectedId, onSelect, onEdit, locale }) => {
  const [view, setView] = useState<View>('story');
  const [query, setQuery] = useState('');
  const isZh = locale === 'zh';
  const sectionMeta = useMemo(
    () => new Map<string, ReturnType<typeof getBoardSections>[number]>(
      getBoardSections(locale).map((section) => [section.type, section])
    ),
    [locale]
  );
  const indexed = useMemo(() => new Map(elements.map((item) => [String(item.id), item])), [elements]);
  const related = useMemo(() => {
    const result = new Map<string, number>();
    connections.forEach((connection) => {
      const source = String(connection.sourceId || '');
      const target = String(connection.targetId || '');
      result.set(source, (result.get(source) || 0) + 1);
      result.set(target, (result.get(target) || 0) + 1);
    });
    return result;
  }, [connections]);
  const counts = useMemo(
    () => ({
      story: elements.filter((item) => groupFor(item.type) === 'story').length,
      people: elements.filter((item) => groupFor(item.type) === 'people').length,
      world: elements.filter((item) => groupFor(item.type) === 'world').length,
      relations: connections.length,
    }),
    [elements, connections]
  );
  const visible = useMemo(() => {
    const search = query.trim().toLocaleLowerCase();
    return elements
      .filter((item) => groupFor(item.type) === view)
      .filter((item) =>
        !search ||
        (titleOf(item) + ' ' + summaryOf(item)).toLocaleLowerCase().includes(search)
      )
      .sort(byNarrativeOrder);
  }, [elements, query, view]);
  const selected = selectedId ? indexed.get(String(selectedId)) : null;
  const sections = useMemo(() => {
    const types =
      view === 'story'
        ? ['scene', 'event', 'dialogue']
        : view === 'people'
          ? ['character', 'arc_milestone']
          : ['world_state', 'theme', 'plot_device'];
    return types
      .map((type) => ({ type, items: visible.filter((item) => normalizeType(item.type) === type) }))
      .filter((section) => section.items.length);
  }, [view, visible]);

  const labels: Record<View, string> = isZh
    ? { story: '剧情脉络', people: '人物弧线', world: '世界与主题', relations: '关系网络' }
    : { story: 'Story', people: 'Characters', world: 'World & themes', relations: 'Connections' };

  const renderCard = (item: any, index: number) => {
    const meta = sectionMeta.get(normalizeType(item.type));
    const data = item.data || {};
    const tags = [
      data.episode || data.episode_number,
      data.act || data.act_type,
      data.beat || data.setting,
    ].filter(Boolean).slice(0, 3);
    return (
      <article
        className={'story-map__card' + (selectedId === item.id ? ' is-selected' : '')}
        key={item.id}
      >
        <button className="story-map__card-main" type="button" onClick={() => onSelect(item.id)}>
          <span className="story-map__index">{String(index + 1).padStart(2, '0')}</span>
          <span className="story-map__card-copy">
            <span className="story-map__card-kicker">{meta?.label || item.type}</span>
            <strong>{titleOf(item) || (isZh ? '未命名' : 'Untitled')}</strong>
            <span className="story-map__summary">
              {summaryOf(item) || (isZh ? '点击编辑，补充这一节点的内容。' : 'Edit this node to add detail.')}
            </span>
            {tags.length > 0 && (
              <span className="story-map__tags">
                {tags.map((tag: unknown, tagIndex: number) => (
                  <span key={tagIndex}>{String(tag)}</span>
                ))}
              </span>
            )}
          </span>
        </button>
        <span className="story-map__card-actions">
          <span>{isZh ? '关联' : 'Links'} {related.get(String(item.id)) || 0}</span>
          <button type="button" onClick={() => onEdit(item)}>
            {isZh ? '编辑' : 'Edit'}
          </button>
        </span>
      </article>
    );
  };

  return (
    <div className="story-map">
      <header className="story-map__toolbar">
        <div>
          <span className="story-map__eyebrow">{isZh ? '叙事工作图' : 'Narrative map'}</span>
          <h3>{labels[view]}</h3>
          <p>{isZh ? '按剧情顺序阅读，选中节点后在右侧编辑。' : 'Read in story order. Select a node to edit it on the right.'}</p>
        </div>
        <label className="story-map__search">
          <span>{isZh ? '查找节点' : 'Find a node'}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={isZh ? '搜索标题或内容' : 'Search title or content'}
          />
        </label>
      </header>
      <div className="story-map__tabs" role="tablist" aria-label={isZh ? '可视化视图' : 'Visualization views'}>
        {(['story', 'people', 'world', 'relations'] as View[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={view === key ? 'is-active' : ''}
            onClick={() => setView(key)}
          >
            {labels[key]} <span>{counts[key]}</span>
          </button>
        ))}
      </div>
      {selected && (
        <div className="story-map__focus">
          <span className="story-map__focus-icon"><StudioIcon name="inspector" /></span>
          <div>
            <small>{isZh ? '当前选中' : 'Selected'}</small>
            <strong>{titleOf(selected)}</strong>
            <p>{summaryOf(selected) || (isZh ? '可以从右侧检查器补充内容。' : 'Add detail in the inspector.')}</p>
          </div>
          <button type="button" onClick={() => onEdit(selected)}>
            {isZh ? '打开编辑' : 'Open editor'}
          </button>
        </div>
      )}
      <div className="story-map__content">
        {view === 'relations' ? (
          connections.length ? (
            <div className="story-map__relations">
              {connections.map((connection, index) => {
                const source = indexed.get(String(connection.sourceId));
                const target = indexed.get(String(connection.targetId));
                return (
                  <div className="story-map__relation" key={connection.id || index}>
                    <button type="button" onClick={() => source && onSelect(source.id)}>
                      {source ? titleOf(source) : String(connection.sourceId)}
                    </button>
                    <span>{String(connection.relationshipType || (isZh ? '关联' : 'Related'))}</span>
                    <button type="button" onClick={() => target && onSelect(target.id)}>
                      {target ? titleOf(target) : String(connection.targetId)}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="story-map__empty">{isZh ? '还没有建立关系。' : 'No connections yet.'}</div>
          )
        ) : sections.length ? (
          sections.map((section) => {
            const meta = sectionMeta.get(section.type);
            return (
              <section className="story-map__section" key={section.type}>
                <div className="story-map__section-head">
                  <span className="story-map__section-icon"><StudioIcon name={meta?.icon || 'board'} /></span>
                  <div><h4>{meta?.label || section.type}</h4><p>{meta?.note}</p></div>
                  <span className="story-map__section-count">{section.items.length}</span>
                </div>
                <div className="story-map__cards">{section.items.map(renderCard)}</div>
              </section>
            );
          })
        ) : (
          <div className="story-map__empty">
            {query ? (isZh ? '没有匹配的节点，试试其他关键词。' : 'No matching nodes.') : (isZh ? '这一部分还没有内容。' : 'No content in this view yet.')}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryMap;
